import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Percentage } from '@ui/components/Amount/Percentage';
import { pageBackgroundStyle } from '@ui/components/Background/PageBackground';
import { BigIntAmount } from '@ui/components/BigIntAmount/BigIntAmount';
import { BigIntAmountUsd } from '@ui/components/BigIntAmount/BigIntAmountUsd';
import { LinkExternal } from '@ui/components/icons';
import { useShouldShowMaintenanceBanner } from '@ui/components/Layout/hooks/useShouldShowMaintenanceBanner';
import { GIT_BOOK_AUDITS } from '@ui/constants/links';
import { ROUTES } from '@ui/constants/routes';
import { LVUSD, USDC } from '@ui/constants/tokens';
import Exchange from '@ui/features/Exchange';
import { RedeemExecutor } from '@ui/features/Exchange/Executors';
import { isAddressInShowProtocolPNL } from '@ui/features/OneClickTrading/constants/allowlist';
import { useDataChainId } from '@ui/hooks/datachain/useDataChainStore';
import { TopNavBar } from '@ui/layouts/components/TopNavBar';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Label } from '@ui/lib/park/ui/styled/field';
import { Text } from '@ui/lib/park/ui/text';
import { TOKEN_ADDRESS_BY_CHAIN } from '@ui/services/contract/constants/address';
import { useTransparencyData } from '@ui/services/contract/hooks/LVToken/useTransparencyData';
import { useLpUnrealizedPnlUsd } from '@ui/services/contract/hooks/TradingCoreFacet/useLpUnrealizedPnlUsd';
import { shortenAddress } from '@ui/utils/formatAddress';
import { getExplorerLink } from '@ui/utils/links';
import BigNumber from 'bignumber.js';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Box, Flex, HStack, styled, VStack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

/**
 * LVUSD page — V2 design system (style/layout only).
 * Data hooks, formulas and displayed values are identical to the V1 page
 * (src/views/LVUSD/index.tsx); only the presentation changed.
 */

const LVUSD_DOCS_LINK =
  'https://leverup.gitbook.io/docs/liquidity-layer/lvusd-stablecoin';

/** Header ghost button — transparent, 1px default border, strong on hover. */
const ghostBtnProps = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  h: '30px',
  px: '12px',
  borderRadius: '2px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'v2.border.default',
  bg: 'transparent',
  color: 'v2.text.muted',
  textStyle: 'v2.label',
  cursor: 'pointer',
  textDecoration: 'none',
  _hover: { borderColor: 'v2.border.strong', color: 'v2.text.primary' },
} as const;

/** V2 card shell for the three action panels. */
const cardProps = {
  bg: 'v2.bg.panel',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'v2.border.default',
  borderRadius: '4px',
  p: '16px',
} as const;

/**
 * Page-scoped V2 overrides for the shared Exchange internals (their source
 * lives outside this view): underline sub-tabs sizing, amount-cell
 * surface/border colors, and mono numerals in the amount inputs.
 * Style-only — no behavior change. Same pattern as LVMON PageV3.
 */
const exchangeV2Css = {
  '& [data-scope=tabs][data-part=list]': { gap: '2px' },
  '& [data-scope=tabs][data-part=trigger]': {
    h: '34px',
    minW: '0',
    px: '12px',
    textStyle: 'v2.label',
  },
  '& [data-scope=field][data-part=root]': {
    background: 'v2.bg.surface1',
    borderColor: 'v2.border.default',
  },
  '& [data-scope=field][data-part=root]:focus-within': {
    borderColor: 'v2.border.focus',
  },
  // Every numeral is tabular mono (V2 rule) — amount inputs included.
  '& [data-scope=field][data-part=input]': {
    fontFamily: 'Inter',
    fontVariantNumeric: 'tabular-nums',
    fontSize: '16px',
    fontWeight: '500',
  },
} as const;

/** 44px CTA per V2 spec (wraps the shared ExecuteButton). */
const ctaCss = {
  '& > button': {
    h: '44px',
    textStyle: 'v2.cta',
  },
} as const;

/**
 * Reskin of the shared swap-direction button (32px square on the seam,
 * surface-2 fill, default border) without touching the shared component.
 */
const switchBtnCss = {
  '& button': {
    background: 'v2.bg.surface2',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'v2.border.default',
    borderRadius: '4px',
    w: '32px',
    h: '32px',
    _hover: {
      background: 'v2.bg.surface3',
      borderColor: 'v2.border.strong',
    },
  },
} as const;

const StatCell = (props: {
  label: React.ReactNode;
  isFirst?: boolean;
  meta?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { label, isFirst, meta, children } = props;

  return (
    <Flex
      direction={{ base: 'row', desktop: 'column' }}
      alignItems={{ base: 'baseline', desktop: 'flex-start' }}
      justifyContent={{ base: 'space-between', desktop: 'flex-start' }}
      gap={{ base: '8px', desktop: '5px' }}
      px={{ base: '16px', desktop: '20px' }}
      py={{ base: '13px', desktop: '18px' }}
      borderTopWidth={{ base: isFirst ? '0px' : '1px', desktop: '0px' }}
      borderLeftWidth={{ base: '0px', desktop: isFirst ? '0px' : '1px' }}
      borderStyle="solid"
      borderColor="v2.border.default"
      minW={0}
    >
      <Text
        textStyle="v2.tableHeader"
        fontSize="10.5px"
        letterSpacing="0.07em"
        color="v2.text.subtle"
      >
        {label}
      </Text>
      {children}
      {meta}
    </Flex>
  );
};

/** Section-label card header with optional right-side mono meta. */
const CardHead = (props: {
  title: React.ReactNode;
  meta?: React.ReactNode;
}) => {
  const { title, meta } = props;

  return (
    <HStack justifyContent="space-between" alignItems="baseline" gap="8px">
      <Text textStyle="v2.sectionLabel" color="v2.text.subtle">
        {title}
      </Text>
      {meta && (
        <Text textStyle="numbers.xsmall" color="v2.text.subtle">
          {meta}
        </Text>
      )}
    </HStack>
  );
};

/** Label-left / mono-value-right row with hairline top divider. */
const InfoRow = (props: {
  label: React.ReactNode;
  isFirst?: boolean;
  children: React.ReactNode;
}) => {
  const { label, isFirst, children } = props;

  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      gap="8px"
      minH="28px"
      borderTopWidth={isFirst ? '0px' : '1px'}
      borderStyle="solid"
      borderColor="v2.border.hairline"
    >
      <Text fontSize="12px" color="v2.text.muted">
        {label}
      </Text>
      <Text textStyle="numbers.small" color="v2.text.primary">
        {children}
      </Text>
    </HStack>
  );
};

/** Token cell of the mint flow visual: icon + symbol + micro caption. */
const FlowToken = (props: { icon: string; symbol: string; caption: string }) => {
  const { icon, symbol, caption } = props;

  return (
    <HStack gap="9px" minW={0}>
      <styled.img src={icon} width="26px" height="26px" flexShrink={0} />
      <VStack gap="1px" alignItems="flex-start" minW={0}>
        <Text fontSize="13px" fontWeight="500" color="v2.text.primary">
          {symbol}
        </Text>
        <Text
          textStyle="v2.meta"
          fontWeight="400"
          color="v2.text.subtle"
          whiteSpace="nowrap"
        >
          {caption}
        </Text>
      </VStack>
    </HStack>
  );
};

export const Page: NextPage = () => {
  const { t } = useTranslation();
  const shouldShowMaintenanceBanner = useShouldShowMaintenanceBanner();
  const router = useRouter();
  const { data: transparencyData, isLoading } = useTransparencyData('LVUSD');
  const { data: lpUnrealizedPnlUsdData } = useLpUnrealizedPnlUsd();
  const { address } = useAccount();
  const chainId = useDataChainId();
  const lvusdAddress = TOKEN_ADDRESS_BY_CHAIN[chainId].LVUSD;

  const isWhiteList = isAddressInShowProtocolPNL(address);

  const lpUnrealizedPnlUsd =
    lpUnrealizedPnlUsdData?.totalUnrealizedPnlUsd || BigNumber(0);
  const lvTokenCirculatingSupply =
    transparencyData?.lvTokenCirculatingSupply || 0n;
  const totalValutValue = transparencyData?.totalValutValue || 0n;

  const collateralRatio =
    lvTokenCirculatingSupply > 0n
      ? parseInt(
          ((totalValutValue * 10000n) / lvTokenCirculatingSupply).toString(),
        ) / 10000
      : 0;

  // V2 ratio semantics: green when healthy, warning <150%, short <120%.
  const collateralRatioColor = isLoading
    ? ''
    : collateralRatio < 1.2
      ? 'v2.short.default'
      : collateralRatio < 1.5
        ? 'v2.warning'
        : 'v2.long.default';

  const statValueProps = {
    textStyle: 'numbers.xxlarge',
    fontSize: { base: '17px', desktop: '28px' },
    letterSpacing: '-0.015em',
  } as const;

  return (
    <>
      <TopNavBar />
      <VStack
        style={pageBackgroundStyle}
        minH={
          shouldShowMaintenanceBanner
            ? 'layout.contentHeightWithAnnouncement'
            : 'layout.contentHeight'
        }
      >
        <Flex
          direction="column"
          w="full"
          maxW="1120px"
          mx="auto"
          px={{ base: '12px', tablet: '24px' }}
          pt={{ base: '20px', tablet: '28px' }}
          pb={{ base: '32px', tablet: '48px' }}
          gap={{ base: '12px', tablet: '16px' }}
        >
          {/* ---- Page header ---- */}
          <Flex
            alignItems="flex-start"
            gap="14px"
            flexWrap={{ base: 'wrap', tablet: 'nowrap' }}
          >
            <styled.img
              src="/tokens/LVUSD.svg"
              width="40px"
              height="40px"
              flexShrink={0}
            />
            <VStack alignItems="flex-start" gap="3px" minW={0}>
              <Text
                as="h1"
                fontSize={{ base: '20px', tablet: '24px' }}
                fontWeight="600"
                letterSpacing="-0.015em"
                lineHeight="1.25"
                color="v2.text.primary"
              >
                {t('lvusd.head.title', 'LeverUp USD')}{' '}
                <styled.span color="v2.text.subtle" fontWeight="500">
                  · LVUSD
                </styled.span>
              </Text>
              <Text
                fontSize="13px"
                lineHeight="1.45"
                color="v2.text.muted"
                maxW="60ch"
              >
                {t(
                  'lvusd.head.subtitle',
                  'Introducing LVUSD, the synthetic stablecoin at the core of LeverUp. All positions are denominated and settled in LVUSD. Use USDC as collateral to mint LVUSD.',
                )}
              </Text>
            </VStack>
            <HStack
              gap="8px"
              flexShrink={0}
              ml={{ base: '0', tablet: 'auto' }}
              w={{ base: 'full', tablet: 'auto' }}
            >
              <styled.button
                type="button"
                {...ghostBtnProps}
                onClick={() => {
                  window.open(
                    getExplorerLink(lvusdAddress, 'token', chainId),
                    '_blank',
                  );
                }}
              >
                <styled.span textStyle="numbers.xsmall">
                  {shortenAddress({ address: lvusdAddress })}
                </styled.span>
                <Icon boxSize="11px">
                  <LinkExternal />
                </Icon>
              </styled.button>
              <styled.a
                {...ghostBtnProps}
                href={GIT_BOOK_AUDITS}
                target="_blank"
                rel="noreferrer"
              >
                {t('lvusd.footer.audit', 'Audit')}
                <Icon boxSize="11px">
                  <LinkExternal />
                </Icon>
              </styled.a>
              <styled.a
                {...ghostBtnProps}
                href={LVUSD_DOCS_LINK}
                target="_blank"
                rel="noreferrer"
              >
                {t('lvusd.head.docs', 'Docs')}
                <Icon boxSize="11px">
                  <LinkExternal />
                </Icon>
              </styled.a>
            </HStack>
          </Flex>

          {/* ---- Stat strip ---- */}
          <Box
            bg="v2.bg.panel"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="v2.border.default"
            borderRadius="4px"
            display="grid"
            gridTemplateColumns={{
              base: '1fr',
              desktop: isWhiteList ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
            }}
          >
            <StatCell
              isFirst
              label={t('lvusd.stats.vaultValue', 'Protocol Vault Value')}
              meta={
                <Text
                  textStyle="numbers.xsmall"
                  fontWeight="400"
                  color="v2.text.subtle"
                  display={{ base: 'none', desktop: 'block' }}
                >
                  {t('lvusd.stats.vaultValueMeta', 'USDC held in vault')}
                </Text>
              }
            >
              <BigIntAmountUsd
                {...statValueProps}
                color="v2.text.primary"
                value={totalValutValue}
              />
            </StatCell>

            <StatCell
              label={t('lvusd.stats.circulating', 'Circulating LVUSD')}
            >
              <BigIntAmount
                {...statValueProps}
                color="v2.text.primary"
                opts={{ decimals: 18 }}
                value={lvTokenCirculatingSupply}
              />
            </StatCell>

            <StatCell
              label={t('lvusd.stats.collateralRatio', 'Collateral Ratio')}
              meta={
                !isLoading &&
                collateralRatio >= 1.5 && (
                  <Text
                    textStyle="numbers.xsmall"
                    fontWeight="400"
                    color="v2.text.subtle"
                    display={{ base: 'none', desktop: 'block' }}
                  >
                    {t(
                      'lvusd.stats.collateralHealthy',
                      'healthy — fully backed above 100%',
                    )}
                  </Text>
                )
              }
            >
              <Percentage
                {...statValueProps}
                color={collateralRatioColor}
                opts={{}}
                value={collateralRatio}
              />
            </StatCell>

            {isWhiteList && (
              <StatCell
                label={t(
                  'lvusd.stats.protocolPnl',
                  'Protocol Unrealized PnL USD',
                )}
              >
                <AmountUsd
                  {...statValueProps}
                  color={
                    lpUnrealizedPnlUsd.isNegative()
                      ? 'v2.short.default'
                      : 'v2.long.default'
                  }
                  value={lpUnrealizedPnlUsd}
                />
              </StatCell>
            )}
          </Box>

          {/* ---- 3-col action grid (mobile priority: Mint → Swap → Provide) ---- */}
          <Box
            display="grid"
            gridTemplateColumns={{ base: '1fr', desktop: 'repeat(3, 1fr)' }}
            gap="8px"
            alignItems="start"
          >
            {/* Mint LVUSD — primary panel */}
            <Flex
              direction="column"
              gap="12px"
              minW={0}
              order={{ base: -2, desktop: 0 }}
              {...cardProps}
              borderColor="v2.long.border"
            >
              <CardHead
                title={t('lvusd.mint.title', 'Mint LVUSD')}
                meta="1 USDC = 1 LVUSD"
              />
              <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                bg="v2.bg.surface1"
                borderWidth="1px"
                borderStyle="solid"
                borderColor="v2.border.default"
                borderRadius="4px"
                px="16px"
                py="14px"
              >
                <FlowToken
                  icon="/tokens/USDC.svg"
                  symbol="USDC"
                  caption={t('lvusd.mint.collateralIn', 'collateral in')}
                />
                <VStack gap="2px" alignItems="center" color="v2.text.subtle">
                  <svg
                    width="20"
                    height="12"
                    viewBox="0 0 24 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    aria-hidden="true"
                  >
                    <path d="M1 6h20m0 0l-5-5m5 5l-5 5" />
                  </svg>
                  <Text textStyle="numbers.micro" color="v2.text.muted">
                    1 : 1
                  </Text>
                </VStack>
                <FlowToken
                  icon="/tokens/LVUSD.svg"
                  symbol="LVUSD"
                  caption={t('lvusd.mint.settlementOut', 'settlement out')}
                />
              </Flex>
              <Text fontSize="12px" fontWeight="500" color="v2.text.muted">
                {t(
                  'lvusd.mint.desc',
                  'Open with USDC collateral, settle in LVUSD',
                )}
              </Text>
              <Button
                onClick={() => {
                  router.push(ROUTES.INDEX);
                }}
                width="full"
                h="44px"
                textStyle="v2.cta"
              >
                {t('lvusd.mint.openPosition', 'Open Position')}
              </Button>
              <Text
                textStyle="v2.meta"
                fontWeight="400"
                lineHeight="1.5"
                color="v2.text.subtle"
              >
                {t(
                  'lvusd.mint.note',
                  'Opening a position with USDC mints LVUSD automatically at execution — no separate step needed.',
                )}
              </Text>
            </Flex>

            {/* Provide Liquidity */}
            <Flex direction="column" gap="12px" minW={0} {...cardProps}>
              <CardHead
                title={t('lvusd.liquidity.title', 'Provide Liquidity')}
                meta="LVUSD/USDC"
              />
              <VStack alignItems="flex-start" gap="4px">
                <Text
        textStyle="v2.tableHeader"
        fontSize="10.5px"
        letterSpacing="0.07em"
        color="v2.text.subtle"
      >
                  {t('lvusd.liquidity.aprLabel', 'APR')}
                </Text>
                <Text
                  textStyle="numbers.xxlarge"
                  letterSpacing="-0.015em"
                  color="v2.long.default"
                >
                  0%
                </Text>
              </VStack>
              <InfoRow label={t('lvusd.liquidity.claimable', 'Claimable')}>
                0 yLV
              </InfoRow>
              <Button disabled width="full" h="44px" textStyle="v2.cta">
                {t('lvusd.liquidity.provide', 'Provide Liquidity')}
              </Button>
              <Button disabled width="full" h="36px" textStyle="v2.cta">
                {t('lvusd.liquidity.claim', 'Claim')}
              </Button>
              <Text
                textStyle="v2.meta"
                fontWeight="400"
                lineHeight="1.5"
                color="v2.text.subtle"
              >
                {t('lvusd.liquidity.desc', 'Provide LVUSD/USDC to earn yLV')}
              </Text>
            </Flex>

            {/* Swap / Redeem */}
            <Flex
              direction="column"
              gap="8px"
              minW={0}
              order={{ base: -1, desktop: 0 }}
              {...cardProps}
            >
              <CardHead title={t('lvusd.exchange.title', 'LVUSD Exchange')} />
              <Box css={exchangeV2Css}>
                <Exchange.Root>
                  <Exchange.Context
                    label="Swap"
                    defaultInputToken={USDC(chainId)}
                    defaultOutputToken={LVUSD(chainId)}
                    overrideInputTokens={[USDC(chainId)]}
                    overrideOutputTokens={[LVUSD(chainId), USDC(chainId)]}
                  >
                    <Exchange.Input />
                    <Box w="full" css={switchBtnCss}>
                      <Exchange.SwitchButton />
                    </Box>
                    <Exchange.Output />
                    <Exchange.InfoCard p="0" minH="28px">
                      <Label
                        fontSize="12px"
                        fontWeight="400"
                        color="v2.text.muted"
                      >
                        Market Price:
                      </Label>
                      <Box textStyle="numbers.small" color="v2.text.primary">
                        <Exchange.MarketPrice />
                      </Box>
                    </Exchange.InfoCard>
                    <Box w="full" css={ctaCss}>
                      <Exchange.ExecuteButton />
                    </Box>
                  </Exchange.Context>
                  <Exchange.Context
                    label="Redeem"
                    defaultInputToken={LVUSD(chainId)}
                    defaultOutputToken={USDC(chainId)}
                    overrideInputTokens={[LVUSD(chainId)]}
                    overrideOutputTokens={[USDC(chainId)]}
                    executor={RedeemExecutor}
                  >
                    <Exchange.Input />
                    <Exchange.Output />
                    <Exchange.InfoCard p="0" minH="28px">
                      <Label
                        fontSize="12px"
                        fontWeight="400"
                        color="v2.text.muted"
                      >
                        TWAP Price:
                      </Label>
                      <Box textStyle="numbers.small" color="v2.text.primary">
                        1 LVUSD = 1 USDC
                      </Box>
                    </Exchange.InfoCard>
                    <Button
                      width="full"
                      mt={1}
                      h="44px"
                      textStyle="v2.cta"
                      disabled={true}
                    >
                      {t('common.Coming Soon')}
                    </Button>
                  </Exchange.Context>
                </Exchange.Root>
              </Box>
            </Flex>
          </Box>

          {/* ---- Page footer meta ---- */}
          <HStack gap="18px" flexWrap="wrap" alignItems="center">
            <Text
              textStyle="numbers.xsmall"
              fontWeight="400"
              color="v2.text.subtle"
            >
              LVUSD · {shortenAddress({ address: lvusdAddress, leadingChars: 6 })}
            </Text>
          </HStack>
        </Flex>
      </VStack>
    </>
  );
};
